import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip
} from '@mui/material';
import {
    formatAverageInterval,
    getAgentStatusClass,
    getAgentStatusText,
    getSeverityLevel,
    getLocalDateInputValue
} from '../../features/calls-analytics/reportPresentation';

const ZoomIdleReport = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [authenticated, setAuthenticated] = useState(true);
    const [authUrl, setAuthUrl] = useState('');
    const [agents, setAgents] = useState([]);
    const [stats, setStats] = useState({
        totalAgents: 0,
        agentsWithCalls: 0,
        totalCalls: 0,
        totalTalkMin: 0,
        totalIdleMin: 0
    });

    // Form state
    const [workDate, setWorkDate] = useState(getLocalDateInputValue());
    const [workStart, setWorkStart] = useState('12:10');
    const [workEnd, setWorkEnd] = useState('19:30');
    const [idleThreshold, setIdleThreshold] = useState(5);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [settingsSaving, setSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState('');
    const [zoomUsers, setZoomUsers] = useState([]);
    const [agentSearch, setAgentSearch] = useState('');
    const [selectedAgentIds, setSelectedAgentIds] = useState([]);
    const [includeAllAgents, setIncludeAllAgents] = useState(true);

    // Check auth on load
    useEffect(() => {
        fetch('/api/zoom-idle/health')
            .then(res => res.json())
            .then(data => {
                if (data.need_auth) {
                    setAuthenticated(false);
                    setAuthUrl(data.auth_url);
                }
            })
            .catch(() => {
                // If health check fails, try running anyway
            });
    }, []);

    const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });
    const eventSourceRef = React.useRef(null);

    const runReport = () => {
        setLoading(true);
        setError('');
        setAgents([]);
        setLoadingProgress({ current: 0, total: 0 });
        setStats({
            totalAgents: 0,
            agentsWithCalls: 0,
            totalCalls: 0,
            totalTalkMin: 0,
            totalIdleMin: 0
        });

        // Close any existing connection
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        // Build query params for SSE stream
        const params = new URLSearchParams({
            work_date: workDate,
            work_start: workStart,
            work_end: workEnd,
            idle_threshold: idleThreshold
        });

        const eventSource = new EventSource(`/api/zoom-idle/stream?${params.toString()}`);
        eventSourceRef.current = eventSource;

        let currentStats = {
            totalAgents: 0,
            agentsWithCalls: 0,
            totalCalls: 0,
            totalTalkMin: 0,
            totalIdleMin: 0
        };

        eventSource.onmessage = (event) => {
            const data = JSON.parse(event.data);

            if (data.error) {
                setError(data.error);
                if (data.need_auth) {
                    setAuthenticated(false);
                    setAuthUrl(data.auth_url);
                }
                eventSource.close();
                setLoading(false);
                return;
            }

            if (data.type === 'init') {
                currentStats.totalAgents = data.total_agents;
                setStats({ ...currentStats });
                setLoadingProgress({ current: 0, total: data.total_agents });
            }
            else if (data.type === 'agent') {
                // Update stats incrementally
                if (data.has_calls) currentStats.agentsWithCalls++;
                currentStats.totalCalls += data.call_count || 0;
                currentStats.totalTalkMin += data.talk_minutes || 0;
                currentStats.totalIdleMin += data.idle_minutes || 0;
                setStats({ ...currentStats });

                // Add agent card - transform from stream format to display format
                const agentData = {
                    'Agent Name': data.name,
                    'Agent Email': data.email,
                    'First Call': data.first_call,
                    'Last Call': data.last_call,
                    'Total Talk Minutes': data.talk_minutes,
                    'Total Idle Minutes (>=5)': data.idle_minutes,
                    'Idle Count': data.idle_count,
                    'Max Single Idle': data.max_idle,
                    'Has Any Calls': data.has_calls,
                    'Avg Call Interval (sec)': data.avg_interval,
                    'Call Count': data.call_count
                };

                setAgents(prev => [...prev, agentData]);
                setLoadingProgress({ current: data.index, total: currentStats.totalAgents });
            }
            else if (data.type === 'done') {
                eventSource.close();
                setLoading(false);
            }
        };

        eventSource.onerror = () => {
            setError('Connection lost. Please try again.');
            eventSource.close();
            setLoading(false);
        };
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    const openAgentSettings = async () => {
        setSettingsOpen(true);
        setSettingsLoading(true);
        setSettingsError('');

        try {
            const response = await fetch('/api/zoom-idle/agent-settings');
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Unable to load agent settings');
            }

            const users = Array.isArray(data.users) ? data.users : [];
            const savedSettings = data.settings || { includeAll: true, selectedAgents: [] };
            const allIds = users.map(user => String(user.id));
            const savedIds = Array.isArray(savedSettings.selectedAgents)
                ? savedSettings.selectedAgents.map(agent => String(agent.id)).filter(Boolean)
                : [];

            setZoomUsers(users);
            setIncludeAllAgents(Boolean(savedSettings.includeAll));
            setSelectedAgentIds(savedSettings.includeAll ? allIds : savedIds);
        } catch (err) {
            setSettingsError(err.message);
        } finally {
            setSettingsLoading(false);
        }
    };

    const toggleAgent = (agentId) => {
        const normalizedId = String(agentId);
        setIncludeAllAgents(false);
        setSelectedAgentIds(prev => (
            prev.includes(normalizedId)
                ? prev.filter(id => id !== normalizedId)
                : [...prev, normalizedId]
        ));
    };

    const selectAllAgents = () => {
        setIncludeAllAgents(false);
        setSelectedAgentIds(zoomUsers.map(user => String(user.id)));
    };

    const clearAllAgents = () => {
        setIncludeAllAgents(false);
        setSelectedAgentIds([]);
    };

    const resetToAllAgents = () => {
        setIncludeAllAgents(true);
        setSelectedAgentIds(zoomUsers.map(user => String(user.id)));
    };

    const saveAgentSettings = async () => {
        setSettingsSaving(true);
        setSettingsError('');

        try {
            const selectedAgents = includeAllAgents
                ? []
                : zoomUsers
                    .filter(user => selectedAgentIds.includes(String(user.id)))
                    .map(user => ({
                        id: user.id,
                        name: user.name,
                        email: user.email
                    }));

            const response = await fetch('/api/zoom-idle/agent-settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    includeAll: includeAllAgents,
                    selectedAgents
                })
            });
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Unable to save agent settings');
            }

            setSettingsOpen(false);
        } catch (err) {
            setSettingsError(err.message);
        } finally {
            setSettingsSaving(false);
        }
    };

    const filteredZoomUsers = zoomUsers.filter(user => {
        const query = agentSearch.trim().toLowerCase();
        if (!query) return true;
        return `${user.name || ''} ${user.email || ''}`.toLowerCase().includes(query);
    });

    // Styles matching Python UI but with SyncFast colors
    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '20px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        },
        title: {
            textAlign: 'center',
            marginBottom: '30px',
            color: '#1976d2',
            fontSize: '2.2em',
            fontWeight: 600
        },
        card: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '25px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0'
        },
        formRow: {
            display: 'flex',
            gap: '15px',
            flexWrap: 'wrap',
            alignItems: 'flex-end'
        },
        formGroup: {
            flex: 1,
            minWidth: '150px'
        },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontWeight: 500,
            color: '#666'
        },
        input: {
            width: '100%',
            padding: '12px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            fontSize: '16px',
            background: '#fafafa'
        },
        button: {
            background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 30px',
            fontSize: '16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 600,
            transition: 'transform 0.2s, box-shadow 0.2s'
        },
        statGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '15px',
            marginBottom: '20px'
        },
        statBox: {
            background: 'rgba(25, 118, 210, 0.08)',
            borderRadius: '8px',
            padding: '20px',
            textAlign: 'center'
        },
        statValue: {
            fontSize: '2em',
            fontWeight: 'bold',
            color: '#1976d2'
        },
        statLabel: {
            color: '#666',
            marginTop: '5px'
        },
        agentsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '16px',
            marginTop: '20px'
        },
        agentCard: {
            background: '#ffffff',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 6px rgba(0,0,0,0.08)',
            transition: 'transform 0.2s, box-shadow 0.2s'
        },
        agentCardHighIdle: {
            borderLeft: '4px solid #b71c1c',
            background: '#fff1f1',
            boxShadow: '0 2px 10px rgba(183, 28, 28, 0.18)'
        },
        agentCardWarningIdle: {
            borderLeft: '4px solid #e65100',
            background: '#fff7ed',
            boxShadow: '0 2px 10px rgba(230, 81, 0, 0.16)'
        },
        agentCardNoCalls: {
            borderLeft: '4px solid #ff9800'
        },
        agentCardOk: {
            borderLeft: '4px solid #00B449'
        },
        agentHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '15px'
        },
        agentName: {
            fontSize: '1.1em',
            fontWeight: 600,
            color: '#333',
            marginBottom: '4px'
        },
        agentEmail: {
            fontSize: '0.85em',
            color: '#888'
        },
        agentStatus: {
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '0.8em',
            fontWeight: 500
        },
        statusHighIdle: {
            background: 'rgba(183, 28, 28, 0.16)',
            color: '#b71c1c'
        },
        statusWarningIdle: {
            background: 'rgba(230, 81, 0, 0.16)',
            color: '#e65100'
        },
        statusNoCalls: {
            background: 'rgba(255, 152, 0, 0.15)',
            color: '#f57c00'
        },
        statusOk: {
            background: 'rgba(0, 180, 73, 0.15)',
            color: '#00B449'
        },
        agentMetrics: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
        },
        metric: {
            background: '#f5f5f5',
            borderRadius: '8px',
            padding: '10px',
            textAlign: 'center'
        },
        metricWarning: {
            background: '#fff3e0',
            border: '1px solid rgba(230, 81, 0, 0.18)'
        },
        metricDanger: {
            background: '#ffebee',
            border: '1px solid rgba(183, 28, 28, 0.2)'
        },
        metricValue: {
            fontSize: '1.3em',
            fontWeight: 'bold',
            color: '#1976d2'
        },
        metricValueWarningOrange: {
            fontSize: '1.3em',
            fontWeight: 'bold',
            color: '#e65100'
        },
        metricValueWarning: {
            fontSize: '1.3em',
            fontWeight: 'bold',
            color: '#b71c1c'
        },
        metricLabel: {
            fontSize: '0.75em',
            color: '#888',
            marginTop: '2px'
        },
        authRequired: {
            textAlign: 'center',
            padding: '40px'
        },
        authLink: {
            color: '#1976d2',
            textDecoration: 'none',
            fontSize: '1.2em'
        }
    };

    // Auth required view
    if (!authenticated) {
        return (
            <Box sx={styles.container}>
                <Typography sx={styles.title}>Zoom Phone Idle Report</Typography>
                <Box sx={{ ...styles.card, ...styles.authRequired }}>
                    <Typography variant="h5" sx={{ mb: 2 }}>Authorization Required</Typography>
                    <Typography sx={{ mb: 3, color: '#666' }}>
                        Click below to authorize with Zoom, then paste the redirect URL:
                    </Typography>
                    <a href={authUrl} target="_blank" rel="noopener noreferrer" style={styles.authLink}>
                        Click here to login to Zoom
                    </a>
                    <Box sx={{ mt: 3 }}>
                        <TextField
                            fullWidth
                            placeholder="Paste the full redirect URL here (http://localhost/?code=...)"
                            sx={{ maxWidth: 500, mb: 2 }}
                        />
                        <Button variant="contained" sx={{ background: '#1976d2' }}>
                            Submit Authorization
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={styles.container}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Typography sx={{ ...styles.title, mb: 0, textAlign: 'left' }}>Zoom Phone Idle Report</Typography>
                <Button variant="outlined" onClick={openAgentSettings}>
                    Manage Agents
                </Button>
            </Box>

            {/* Controls Card */}
            <Box sx={styles.card}>
                <Box sx={styles.formRow}>
                    <Box sx={styles.formGroup}>
                        <label style={styles.label}>Date</label>
                        <input
                            type="date"
                            value={workDate}
                            onChange={(e) => setWorkDate(e.target.value)}
                            style={styles.input}
                        />
                    </Box>
                    <Box sx={styles.formGroup}>
                        <label style={styles.label}>Work Start</label>
                        <input
                            type="time"
                            value={workStart}
                            onChange={(e) => setWorkStart(e.target.value)}
                            style={styles.input}
                        />
                    </Box>
                    <Box sx={styles.formGroup}>
                        <label style={styles.label}>Work End</label>
                        <input
                            type="time"
                            value={workEnd}
                            onChange={(e) => setWorkEnd(e.target.value)}
                            style={styles.input}
                        />
                    </Box>
                    <Box sx={styles.formGroup}>
                        <label style={styles.label}>Idle Threshold (min)</label>
                        <input
                            type="number"
                            value={idleThreshold}
                            onChange={(e) => setIdleThreshold(parseInt(e.target.value) || 5)}
                            min="1"
                            style={styles.input}
                        />
                    </Box>
                    <Box sx={styles.formGroup}>
                        <Button
                            onClick={runReport}
                            disabled={loading}
                            sx={{
                                ...styles.button,
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 5px 20px rgba(25,118,210,0.4)'
                                },
                                '&:disabled': {
                                    background: '#ccc',
                                    cursor: 'not-allowed'
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : null}
                            Generate Report
                        </Button>
                    </Box>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                )}
            </Box>

            {/* Stats */}
            {agents.length > 0 && (
                <Box sx={styles.card}>
                    <Box sx={styles.statGrid}>
                        <Box sx={styles.statBox}>
                            <Box sx={styles.statValue}>{stats.totalAgents}</Box>
                            <Box sx={styles.statLabel}>Total Agents</Box>
                        </Box>
                        <Box sx={styles.statBox}>
                            <Box sx={styles.statValue}>{stats.agentsWithCalls}</Box>
                            <Box sx={styles.statLabel}>Agents with Calls</Box>
                        </Box>
                        <Box sx={styles.statBox}>
                            <Box sx={styles.statValue}>{stats.totalCalls}</Box>
                            <Box sx={styles.statLabel}>Total Calls</Box>
                        </Box>
                        <Box sx={styles.statBox}>
                            <Box sx={styles.statValue}>{Math.round(stats.totalTalkMin)}</Box>
                            <Box sx={styles.statLabel}>Total Talk (min)</Box>
                        </Box>
                        <Box sx={styles.statBox}>
                            <Box sx={styles.statValue}>{Math.round(stats.totalIdleMin)}</Box>
                            <Box sx={styles.statLabel}>Total Idle (min)</Box>
                        </Box>
                    </Box>

                    {/* Agent Cards Grid */}
                    <Box sx={styles.agentsGrid}>
                        {agents.map((agent, idx) => {
                            const status = getAgentStatusClass(agent);
                            const statusText = getAgentStatusText(agent);
                            const idleMinutes = agent['Total Idle Minutes (>=5)'] || 0;
                            const avgInterval = agent['Avg Call Interval (sec)'] || 0;
                            const idleSeverity = getSeverityLevel(idleMinutes, 60, 80);
                            const intervalSeverity = getSeverityLevel(avgInterval, 120, 240);

                            const cardStyle = {
                                ...styles.agentCard,
                                ...(status === 'high-idle' ? styles.agentCardHighIdle : {}),
                                ...(status === 'warning-idle' ? styles.agentCardWarningIdle : {}),
                                ...(status === 'no-calls' ? styles.agentCardNoCalls : {}),
                                ...(status === 'ok' ? styles.agentCardOk : {})
                            };

                            const statusStyle = {
                                ...styles.agentStatus,
                                ...(status === 'high-idle' ? styles.statusHighIdle : {}),
                                ...(status === 'warning-idle' ? styles.statusWarningIdle : {}),
                                ...(status === 'no-calls' ? styles.statusNoCalls : {}),
                                ...(status === 'ok' ? styles.statusOk : {})
                            };

                            const getMetricBoxStyle = (severity) => ({
                                ...styles.metric,
                                ...(severity === 'warning' ? styles.metricWarning : {}),
                                ...(severity === 'danger' ? styles.metricDanger : {})
                            });

                            const getMetricValueStyle = (severity) => {
                                if (severity === 'danger') return styles.metricValueWarning;
                                if (severity === 'warning') return styles.metricValueWarningOrange;
                                return styles.metricValue;
                            };

                            return (
                                <Box key={idx} sx={cardStyle}>
                                    <Box sx={styles.agentHeader}>
                                        <Box>
                                            <Box sx={styles.agentName}>{agent['Agent Name']}</Box>
                                            <Box sx={styles.agentEmail}>{agent['Agent Email']}</Box>
                                        </Box>
                                        <Box sx={statusStyle}>{statusText}</Box>
                                    </Box>
                                    <Box sx={styles.agentMetrics}>
                                        <Box sx={styles.metric}>
                                            <Box sx={styles.metricValue}>{agent['Call Count'] || 0}</Box>
                                            <Box sx={styles.metricLabel}>Calls</Box>
                                        </Box>
                                        <Box sx={styles.metric}>
                                            <Box sx={styles.metricValue}>{agent['First Call'] || '-'}</Box>
                                            <Box sx={styles.metricLabel}>First Call</Box>
                                        </Box>
                                        <Box sx={styles.metric}>
                                            <Box sx={styles.metricValue}>{agent['Last Call'] || '-'}</Box>
                                            <Box sx={styles.metricLabel}>Last Call</Box>
                                        </Box>
                                        <Box sx={styles.metric}>
                                            <Box sx={styles.metricValue}>{Math.round(agent['Total Talk Minutes'] || 0)}</Box>
                                            <Box sx={styles.metricLabel}>Talk Mins</Box>
                                        </Box>
                                        <Box sx={getMetricBoxStyle(idleSeverity)}>
                                            <Box sx={getMetricValueStyle(idleSeverity)}>
                                                {Math.round(idleMinutes)}
                                            </Box>
                                            <Box sx={styles.metricLabel}>Idle Mins</Box>
                                        </Box>
                                        <Box sx={styles.metric}>
                                            <Box sx={styles.metricValue}>{agent['Idle Count'] || 0}</Box>
                                            <Box sx={styles.metricLabel}>Idle Count</Box>
                                        </Box>
                                        <Box sx={styles.metric}>
                                            <Box sx={styles.metricValue}>{Math.round(agent['Max Single Idle'] || 0)}</Box>
                                            <Box sx={styles.metricLabel}>Max Idle</Box>
                                        </Box>
                                        <Box sx={getMetricBoxStyle(intervalSeverity)}>
                                            <Box sx={getMetricValueStyle(intervalSeverity)}>{formatAverageInterval(avgInterval)}</Box>
                                            <Box sx={styles.metricLabel}>Avg Interval</Box>
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Loading state */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                    <Typography sx={{ mt: 2, color: '#666' }}>
                        {loadingProgress.total > 0
                            ? `Loading agents... (${loadingProgress.current}/${loadingProgress.total})`
                            : 'Connecting to Zoom API...'}
                    </Typography>
                </Box>
            )}

            <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    Agent Settings
                    <Typography variant="body2" color="text.secondary">
                        Choose which Zoom Phone users are included in Calls Analytics reports.
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {settingsError && <Alert severity="error" sx={{ mb: 2 }}>{settingsError}</Alert>}

                    {settingsLoading ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ mt: 2, color: '#666' }}>Loading Zoom Phone users...</Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                <Chip
                                    label={includeAllAgents ? 'Dynamic all users' : `${selectedAgentIds.length} selected`}
                                    color={includeAllAgents ? 'primary' : 'default'}
                                    size="small"
                                />
                                <Typography variant="body2" color="text.secondary">
                                    {zoomUsers.length} Zoom Phone users found
                                </Typography>
                            </Box>

                            <TextField
                                fullWidth
                                label="Search agents"
                                value={agentSearch}
                                onChange={(e) => setAgentSearch(e.target.value)}
                                sx={{ mb: 2 }}
                            />

                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                                <Button size="small" variant="outlined" onClick={selectAllAgents}>Select All</Button>
                                <Button size="small" variant="outlined" onClick={clearAllAgents}>Clear All</Button>
                                <Button size="small" variant="text" onClick={resetToAllAgents}>Reset to All</Button>
                            </Box>

                            <Divider sx={{ mb: 1 }} />

                            <List dense sx={{ maxHeight: 420, overflow: 'auto' }}>
                                {filteredZoomUsers.map(user => {
                                    const userId = String(user.id);
                                    const checked = selectedAgentIds.includes(userId);

                                    return (
                                        <ListItem
                                            key={userId}
                                            button
                                            onClick={() => toggleAgent(userId)}
                                            secondaryAction={
                                                <Checkbox
                                                    edge="end"
                                                    checked={checked}
                                                    onClick={(event) => event.stopPropagation()}
                                                    onChange={() => toggleAgent(userId)}
                                                />
                                            }
                                        >
                                            <ListItemText
                                                primary={user.name || user.email || 'Unnamed Zoom user'}
                                                secondary={user.email || 'No email available'}
                                            />
                                        </ListItem>
                                    );
                                })}
                                {!filteredZoomUsers.length && (
                                    <Typography sx={{ py: 3, textAlign: 'center', color: '#666' }}>
                                        No Zoom Phone users match this search.
                                    </Typography>
                                )}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSettingsOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={saveAgentSettings}
                        disabled={settingsLoading || settingsSaving}
                    >
                        {settingsSaving ? <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} /> : null}
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoomIdleReport;
